// @flow

// 번역자 화면 상단의 연결 상태 표시등 (#204).
// 평소에는 작은 점 하나만 보인다. 확인 요청이 성공할 때마다 초록 점이 한 번 깜빡여
// "지금 살아 있다"를 조용히 보여 주고, 자세한 상태는 마우스를 올리면 나온다.
// 문제가 생겼을 때 크게 알리는 건 Input 위 띠(ConnectionBanner)가 맡는다.

import React from "react";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import "./ConnectionIndicator.css";
import {
  useConnectionStatus,
  connectionLevel,
  FAILURES_TO_DROP,
  PROBE_INTERVAL_MS,
} from "../utils/ConnectionStatus";
import type { ConnectionStatus, ConnectionLevel } from "../utils/ConnectionStatus";
import { useSendStatus } from "../utils/MessageSender";
import type { SendStatus } from "../utils/MessageSender";

const DOT_COLOR = {
  ok: "#34c759",
  degraded: "#ffcc00",
  down: "#ff3b30",
  unknown: "#a0a0a0",
};

const styles = {
  // 점은 작게, 마우스를 올릴 영역은 조금 넉넉하게
  hitArea: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "middle",
    marginLeft: "0.5rem",
    width: "18px",
    height: "18px",
    cursor: "default",
  },
  dot: {
    width: "6px",
    height: "6px",
    borderRadius: "50%",
  },
  tooltipBody: {
    textAlign: "left",
  },
};

export function formatRtt(ms: number): string {
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}초` : `${ms}ms`;
}

export type ConnectionView = {
  status: ConnectionStatus,
  send: SendStatus,
  level: ConnectionLevel,
  label: string,
};

// 표시등과 Input 위 띠가 같은 판단을 쓰도록 한곳에서 계산한다
export function useConnectionView(): ConnectionView {
  const status = useConnectionStatus();
  const send = useSendStatus();
  const level = connectionLevel(status, send.retrying);

  let label;
  if (level === "down") {
    label = "연결 끊김";
  } else if (level === "unknown") {
    label = "연결 확인 중";
  } else if (status.appsyncRetrying > 0 || status.mqttRetrying > 0 || status.failures > 0) {
    label = "재연결 중";
  } else if (send.retrying) {
    label = "전송 재시도 중";
  } else {
    label = `연결됨 · ${formatRtt(status.rttMs ?? 0)}`;
  }
  return { status, send, level, label };
}

export default function ConnectionIndicator(): React$Element<any> {
  const { status, send, level, label } = useConnectionView();

  const lines = [
    ["네트워크", status.online ? "온라인" : "오프라인"],
    [
      "서버 응답",
      !status.online
        ? "오프라인이라 확인 멈춤"
        : status.failures > 0
        ? `응답 없음 (${status.failures}회 연속${
            status.failures >= FAILURES_TO_DROP ? ", 다시 연결 중" : ""
          })`
        : status.rttMs != null
        ? formatRtt(status.rttMs)
        : "확인 중",
    ],
    [
      "자막 수신 (History·송출)",
      status.appsyncRetrying > 0 ? `재연결 대기 ${status.appsyncRetrying}개` : "정상",
    ],
    [
      "파트너 연결",
      status.mqttRetrying > 0 ? `재연결 대기 ${status.mqttRetrying}개` : "정상",
    ],
    [
      "전송",
      send.failed.length > 0
        ? `실패 ${send.failed.length}건 (Input에서 재전송)`
        : send.retrying
        ? "재시도 중"
        : "정상",
    ],
  ];

  return (
    <OverlayTrigger
      placement="bottom"
      overlay={
        <Tooltip id="connection-indicator-tooltip">
          <div style={styles.tooltipBody}>
            <div className="fw-bold mb-1">{label}</div>
            {lines.map(([name, value]) => (
              <div key={name}>
                {name}: {value}
              </div>
            ))}
            <div className="mt-1" style={{ opacity: 0.7 }}>
              {PROBE_INTERVAL_MS / 1000}초마다 서버에 확인 요청을 보냅니다
            </div>
          </div>
        </Tooltip>
      }
    >
      <span
        style={styles.hitArea}
        role="status"
        aria-label={label}
        data-testid="connection-indicator"
        data-level={level}
      >
        <span
          // 확인이 성공할 때마다 key가 바뀌어 깜빡임 애니메이션이 다시 돈다
          key={level === "ok" ? status.lastOkAt ?? 0 : level}
          className={level === "ok" ? "connection-dot-beat" : undefined}
          style={{ ...styles.dot, backgroundColor: DOT_COLOR[level] }}
        />
      </span>
    </OverlayTrigger>
  );
}
